import type { SeriesRef, StudyRef } from '../../../dicomweb';
import { type KeyImageIn, RisError } from '../../client';
import type { AssistantFinding } from '../api';
import { mm } from '../tools/dom';
import { formatChange } from '../tools/geometry';
import {
  type FollowupSuggestion, type IssueKind, type IssueSeverity, type RecistTable, type ReportIssue, type ToolKey, type ToolSwitches,
  MAX_INSERT_TEXT, MAX_PATIENT_SUMMARY, RECIST_RESPONSES, type RecistResponse, type ReportSection, SECTION_LABELS,
} from './shapes';

/**
 * The pure decisions behind the radiologist tools: which tools show for this session, whether
 * "Enviar al informe" sends or copies, how a ris refusal is worded, and the bodies the tools post.
 * No DOM and no session state here, so e2e/assistant-radiologist-unit.spec.mjs checks it in node.
 */

export const RIS_PERM = {
  reportsRead: 'ris:reports:read',
  reportsWrite: 'ris:reports:write',
  reportsSign: 'ris:reports:sign',
  annotate: 'viewer:annotate',
  review: 'viewer:ai:review',
} as const;

/** What each tool needs from the ticket besides viewer:ai:run (the routes check it again). */
const TOOL_PERMS: Record<ToolKey, readonly string[]> = {
  send_to_report: [RIS_PERM.reportsWrite],
  report_check: [RIS_PERM.reportsRead],
  report_compose: [RIS_PERM.reportsWrite],
  patient_context: [],
  prepare_study: [],
  followups: [],
  recist: [],
  key_images: [RIS_PERM.annotate],
  patient_summary: [RIS_PERM.reportsRead],
};

/** A tool shows when the org's AI is on, its switch is on and the session holds what it needs. */
export function toolAvailable(switches: ToolSwitches, perms: readonly string[], key: ToolKey): boolean {
  return switches.enabled && switches.tools[key] && TOOL_PERMS[key].every((p) => perms.includes(p));
}

export type SendMode = 'send' | 'copy';

/**
 * "Enviar al informe" (into the reader's draft) when the tool is on and the ticket may write reports;
 * otherwise the text goes to the clipboard as before ("Copiar al informe").
 */
export function sendMode(switches: ToolSwitches, perms: readonly string[]): SendMode {
  return toolAvailable(switches, perms, 'send_to_report') ? 'send' : 'copy';
}

export const SEND_LABEL: Record<SendMode, string> = { send: 'Enviar al informe', copy: 'Copiar al informe' };

// --- what is sent ---------------------------------------------------------------------------------

/** One piece of text for one section of the reader's draft. */
export interface ReportPart {
  section: ReportSection;
  text: string;
}

/** Empty parts dropped, each cut to what one insert accepts. */
export function cleanParts(parts: readonly ReportPart[]): ReportPart[] {
  return parts.map((p) => ({ section: p.section, text: p.text.trim().slice(0, MAX_INSERT_TEXT) })).filter((p) => p.text.length > 0);
}

/** "Hallazgos:\n…" per part: what the send confirmation shows, section by section. */
export function partsText(parts: readonly ReportPart[]): string {
  return parts.map((p) => `${SECTION_LABELS[p.section]}:\n${p.text}`).join('\n\n');
}

/** What the clipboard gets: one part's text as it is (as before), several with their section names. */
export function clipboardText(parts: readonly ReportPart[]): string {
  return parts.length === 1 ? parts[0].text : partsText(parts);
}

// --- ris refusals -----------------------------------------------------------------------------------

export const TOOL_ERROR_TEXT = {
  disabled: 'Esta herramienta del Asistente Guru está desactivada para la organización.',
  coreOnly: 'Esta herramienta solo está disponible para exámenes del RIS principal.',
  noDraft: 'No tiene un borrador de informe para este estudio.',
  noSignedReport: 'El estudio no tiene un informe firmado vigente.',
  notDraftable: 'El informe de este estudio ya está firmado y no admite cambios desde el visor.',
  forbidden: 'No dispone de permisos para esta herramienta del Asistente Guru.',
  notOwner: 'El borrador de este estudio pertenece a otro radiólogo.',
  notAccepted: 'Primero acepte o corrija el hallazgo para pedir un seguimiento.',
  notSendable: 'El texto no se pudo depurar de datos del paciente con certeza; no se envió nada.',
} as const;

const ERROR_CODES: Array<[code: string, text: string]> = [
  ['errors.ai.tool_disabled', TOOL_ERROR_TEXT.disabled],
  ['errors.ai.core_only', TOOL_ERROR_TEXT.coreOnly],
  ['errors.ai.no_draft', TOOL_ERROR_TEXT.noDraft],
  ['errors.ai.no_signed_report', TOOL_ERROR_TEXT.noSignedReport],
  ['errors.reports.not_draftable', TOOL_ERROR_TEXT.notDraftable],
  ['errors.reports.wrong_state', TOOL_ERROR_TEXT.notDraftable],
  ['errors.reading.not_owner', TOOL_ERROR_TEXT.notOwner],
  ['errors.ai.finding_not_accepted', TOOL_ERROR_TEXT.notAccepted],
  ['errors.ai.not_sendable', TOOL_ERROR_TEXT.notSendable],
];

/** True for the 409 a switched-off tool answers. */
export function isToolDisabled(error: unknown): boolean {
  return error instanceof RisError && error.detail.includes('errors.ai.tool_disabled');
}

/** The Spanish text for a round-3 refusal, or null (the caller then uses the Asistente's usual wording). */
export function toolErrorText(error: unknown): string | null {
  if (!(error instanceof RisError)) return null;
  const hit = ERROR_CODES.find(([code]) => error.detail.includes(code));
  if (hit) return hit[1];
  return error.status === 401 || error.status === 403 ? TOOL_ERROR_TEXT.forbidden : null;
}

// --- dates ------------------------------------------------------------------------------------------

/** "DD/MM/AAAA" from DICOM `YYYYMMDD` or ISO `YYYY-MM-DD…`; "—" when unreadable. */
export function dateText(value: string | null | undefined): string {
  const digits = (value ?? '').slice(0, 10).replace(/-/g, '');
  return /^\d{8}$/.test(digits) ? `${digits.slice(6, 8)}/${digits.slice(4, 6)}/${digits.slice(0, 4)}` : '—';
}

// --- RECIST -----------------------------------------------------------------------------------------

export const RECIST_LABELS: Record<RecistResponse, string> = {
  CR: 'Respuesta completa (RC)',
  PR: 'Respuesta parcial (RP)',
  SD: 'Enfermedad estable (EE)',
  PD: 'Progresión de la enfermedad (PE)',
  NE: 'No evaluable (NE)',
};

export function recistLabel(response: string): string {
  return (RECIST_RESPONSES as readonly string[]).includes(response) ? RECIST_LABELS[response as RecistResponse] : RECIST_LABELS.NE;
}

export interface RecistGrid {
  /** One per timepoint: the date and what it is (base / nadir / actual). */
  columns: Array<{ date: string; roles: string[]; complete: boolean }>;
  rows: Array<{ label: string; cells: string[] }>;
  sums: string[];
}

/** The table a RECIST answer is drawn as: lesions down, timepoints across, the sum at the bottom. */
export function recistGrid(table: RecistTable): RecistGrid {
  const roles = (i: number) => [
    table.baseline === i ? 'base' : null, table.nadir === i ? 'nadir' : null, table.current === i ? 'actual' : null,
  ].filter((r): r is string => r !== null);
  return {
    columns: table.timepoints.map((t, i) => ({ date: dateText(t.date), roles: roles(i), complete: t.complete })),
    rows: table.targets.map((target) => ({ label: target.label, cells: table.timepoints.map((t) => mm(t.lesions[target.id] ?? null)) })),
    sums: table.timepoints.map((t) => mm(t.sumMm)),
  };
}

/** "Respuesta: … · desde la base −32.0 % · desde el nadir +4.0 %". */
export function recistSummaryLine(table: RecistTable): string {
  const parts = [`Respuesta: ${recistLabel(table.response)}`];
  if (table.changeFromBaselinePct !== null) parts.push(`desde la base ${formatChange(table.changeFromBaselinePct)}`);
  if (table.changeFromNadirPct !== null) parts.push(`desde el nadir ${formatChange(table.changeFromNadirPct)}`);
  return parts.join(' · ');
}

// --- report check ----------------------------------------------------------------------------------

export const ISSUE_KIND_LABELS: Record<IssueKind, string> = {
  laterality: 'Lateralidad',
  missing_finding: 'Hallazgo no incluido',
  units: 'Unidades',
  contradiction: 'Contradicción',
  critical_uncommunicated: 'Crítico sin comunicar',
  other: 'Observación',
};

export const ISSUE_SEVERITY_LABELS: Record<IssueSeverity, string> = { critical: 'Crítico', warning: 'Advertencia', info: 'Nota' };

/** "Advertencia · Lateralidad · Impresión" (the section only when the issue names one). */
export function issueHead(issue: ReportIssue): string {
  const parts = [ISSUE_SEVERITY_LABELS[issue.severity], ISSUE_KIND_LABELS[issue.kind]];
  if (issue.section) parts.push(SECTION_LABELS[issue.section]);
  return parts.join(' · ');
}

// --- request bodies ---------------------------------------------------------------------------------

export interface PrepareBody {
  series: Array<{ ref: string; description: string; modality: string; images: number }>;
  priors: Array<{ study_uid: string; date: string | null; description: string }>;
}

const MAX_PREPARE_SERIES = 40;
const MAX_PREPARE_PRIORS = 10;

/** What "Preparar estudio" tells ris: the study's image series and the priors in the study browser. */
export function prepareBody(series: readonly SeriesRef[], priors: readonly StudyRef[]): PrepareBody {
  const images = (s: SeriesRef) => s.instances.reduce((sum, i) => sum + (i.numberOfFrames || 1), 0) || s.instanceCount || 0;
  const date = (d: string) => (/^\d{8}$/.test(d) ? d : null);
  return {
    series: series.slice(0, MAX_PREPARE_SERIES).map((s) => ({
      ref: s.seriesInstanceUID.slice(0, 64),
      description: (s.description || `Serie ${s.seriesNumber}`).slice(0, 120),
      modality: (s.modality || '').slice(0, 16),
      images: images(s),
    })),
    priors: [...priors].sort((a, b) => (b.studyDate || '').localeCompare(a.studyDate || '')).slice(0, MAX_PREPARE_PRIORS).map((p) => ({
      study_uid: p.studyInstanceUID,
      date: date(p.studyDate),
      description: (p.studyDescription || '').slice(0, 120),
    })),
  };
}

/** The finding's text as reviewed: the correction when corrected, else its label. */
export function reviewedLabel(finding: AssistantFinding): string {
  return finding.status === 'corrected' && finding.correction ? finding.correction : finding.label;
}

export function isReviewed(finding: AssistantFinding): boolean {
  return finding.status === 'accepted' || finding.status === 'corrected';
}

export interface ImageTarget {
  seriesUid: string;
  sopUid: string;
  frame: number;
  index: number;
  seriesDescription: string | null;
}

const MAX_KEY_DESCRIPTION = 255;

/** `PUT key-images` for a reviewed finding (§2.8): its image, described by the reviewed label. */
export function keyImageBody(finding: AssistantFinding, target: ImageTarget): KeyImageIn {
  return {
    series_uid: target.seriesUid,
    sop_uid: target.sopUid,
    frame: target.frame,
    slice: target.index + 1,
    series_description: target.seriesDescription,
    description: reviewedLabel(finding).slice(0, MAX_KEY_DESCRIPTION),
  };
}

export interface FollowupBody {
  finding_id: string | null;
  due_in_months: number;
  modality: string;
  reason: string;
  guideline: string;
}

/** `POST followups` from a suggestion, or null when the guideline asks for none (interval 0). */
export function followupBody(finding: AssistantFinding, suggestion: FollowupSuggestion): FollowupBody | null {
  if (suggestion.intervalMonths < 1) return null;
  // ris finding ids are uuids; anything else goes as null (the follow-up then has no finding link).
  const id = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(finding.id) ? finding.id.toLowerCase() : null;
  return {
    finding_id: id,
    due_in_months: Math.min(60, suggestion.intervalMonths),
    modality: (suggestion.modality ?? '').slice(0, 16),
    reason: (suggestion.text ?? reviewedLabel(finding)).slice(0, 500),
    guideline: (suggestion.guideline ?? '').slice(0, 120),
  };
}

/** The approved summary may be saved: 1–4000 characters after trimming. */
export function summaryApprovable(text: string): boolean {
  const trimmed = text.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_PATIENT_SUMMARY;
}
